import React, { useState } from 'react';
import {
  Wand2, ChevronDown, ChevronUp, Zap, Music,
  ArrowRight, Clock, Sparkles
} from 'lucide-react';
import './MixPointAdvisor.css';

// Mix type recommendations based on track characteristics
const getMixAdvice = (fromTrack, toTrack) => {
  if (!fromTrack || !toTrack) return null;

  const fromBpm = fromTrack.bpm_override || fromTrack.bpm || 0;
  const toBpm = toTrack.bpm_override || toTrack.bpm || 0;
  const fromKey = fromTrack.key_override || fromTrack.key;
  const toKey = toTrack.key_override || toTrack.key;
  const fromEnergy = fromTrack.energy_override || fromTrack.energy || 5;
  const toEnergy = toTrack.energy_override || toTrack.energy || 5;

  const bpmDiff = Math.abs(fromBpm - toBpm);
  const energyDiff = toEnergy - fromEnergy;
  
  // Determine mix type
  let mixType = 'blend';
  let mixDuration = '16 bars';
  let technique = [];
  let difficulty = 'easy';
  let tips = [];

  // BPM considerations
  if (bpmDiff === 0) {
    tips.push('BPMs match perfectly - smooth transition guaranteed');
    technique.push('phrase-match');
  } else if (bpmDiff <= 2) {
    tips.push(`Minor BPM difference (${bpmDiff} BPM) - gradual pitch adjustment`);
    technique.push('tempo-sync');
  } else if (bpmDiff <= 5) {
    tips.push(`BPM difference of ${bpmDiff} - consider tempo riding or quick cut`);
    technique.push('tempo-ride');
    difficulty = 'medium';
  } else {
    tips.push(`Significant BPM gap (${bpmDiff}) - use breakdown or echo out`);
    mixType = 'cut';
    mixDuration = '8 bars';
    technique.push('breakdown-mix');
    difficulty = 'hard';
  }

  // Key considerations
  if (fromKey && toKey) {
    const isHarmonic = isHarmonicMatch(fromKey, toKey);
    if (isHarmonic) {
      tips.push('Keys are harmonically compatible - blend freely');
    } else {
      tips.push('Non-harmonic keys - use filter sweep or quick cut');
      if (!technique.includes('filter-sweep')) {
        technique.push('filter-sweep');
      }
      difficulty = difficulty === 'easy' ? 'medium' : difficulty;
    }
  }

  // Energy considerations
  if (energyDiff > 2) {
    tips.push('Energy building - extend the mix, let it breathe');
    mixDuration = '32 bars';
    technique.push('energy-build');
  } else if (energyDiff < -2) {
    tips.push('Energy dropping - consider breakdown or filter out');
    technique.push('filter-out');
  } else {
    tips.push('Similar energy levels - smooth blend works great');
  }

  // Determine best mix approach
  if (technique.includes('breakdown-mix') || bpmDiff > 8) {
    mixType = 'cut';
    mixDuration = '4-8 bars';
  } else if (technique.includes('filter-sweep') && !technique.includes('phrase-match')) {
    mixType = 'filter';
    mixDuration = '16 bars';
  } else if (technique.includes('energy-build')) {
    mixType = 'extended';
    mixDuration = '32 bars';
  }

  // Mix point suggestions
  const mixPoints = {
    out: getMixOutPoint(fromTrack, toTrack),
    in: getMixInPoint(fromTrack, toTrack)
  };

  return {
    mixType,
    mixDuration,
    technique,
    difficulty,
    tips,
    mixPoints,
    fromBpm,
    toBpm,
    bpmDiff,
    energyDiff
  };
};

// Check if keys are harmonically compatible (simplified Camelot)
const isHarmonicMatch = (key1, key2) => {
  if (!key1 || !key2) return true; // Assume compatible if unknown
  
  const camelotMap = {
    '1A': ['1A', '12A', '2A', '1B'],
    '2A': ['2A', '1A', '3A', '2B'],
    '3A': ['3A', '2A', '4A', '3B'],
    '4A': ['4A', '3A', '5A', '4B'],
    '5A': ['5A', '4A', '6A', '5B'],
    '6A': ['6A', '5A', '7A', '6B'],
    '7A': ['7A', '6A', '8A', '7B'],
    '8A': ['8A', '7A', '9A', '8B'],
    '9A': ['9A', '8A', '10A', '9B'],
    '10A': ['10A', '9A', '11A', '10B'],
    '11A': ['11A', '10A', '12A', '11B'],
    '12A': ['12A', '11A', '1A', '12B'],
    '1B': ['1B', '12B', '2B', '1A'],
    '2B': ['2B', '1B', '3B', '2A'],
    '3B': ['3B', '2B', '4B', '3A'],
    '4B': ['4B', '3B', '5B', '4A'],
    '5B': ['5B', '4B', '6B', '5A'],
    '6B': ['6B', '5B', '7B', '6A'],
    '7B': ['7B', '6B', '8B', '7A'],
    '8B': ['8B', '7B', '9B', '8A'],
    '9B': ['9B', '8B', '10B', '9A'],
    '10B': ['10B', '9B', '11B', '10A'],
    '11B': ['11B', '10B', '12B', '11A'],
    '12B': ['12B', '11B', '1B', '12A']
  };

  return camelotMap[key1]?.includes(key2) || false;
};

// Suggest mix-out point
const getMixOutPoint = (fromTrack, toTrack) => {
  const fromDuration = fromTrack.duration_ms || 0;
  const minutes = Math.floor(fromDuration / 60000);
  
  // Suggest mix-out at last 1/4 of track or at breakdown
  const suggestedTime = Math.floor(fromDuration * 0.75 / 1000);
  const suggestedMinutes = Math.floor(suggestedTime / 60);
  const suggestedSeconds = suggestedTime % 60;
  
  return {
    time: `${suggestedMinutes}:${suggestedSeconds.toString().padStart(2, '0')}`,
    description: 'Start mixing out during the outro or last breakdown'
  };
};

// Suggest mix-in point
const getMixInPoint = (fromTrack, toTrack) => {
  return {
    time: '0:00 - 0:32',
    description: 'Bring in during the intro, before main elements kick in'
  };
};

// Mix type display info
const MIX_TYPES = {
  blend: { label: 'Smooth Blend', icon: '🔀', color: '#4ade80' },
  cut: { label: 'Quick Cut', icon: '⚡', color: '#f59e0b' },
  filter: { label: 'Filter Sweep', icon: '🌊', color: '#3b82f6' },
  extended: { label: 'Extended Mix', icon: '🎵', color: '#c084fc' }
};

const DIFFICULTY_COLORS = {
  easy: '#4ade80',
  medium: '#f59e0b',
  hard: '#ef4444'
};

const MixPointAdvisor = ({ fromTrack, toTrack, isExpanded, onToggle }) => {
  const advice = getMixAdvice(fromTrack, toTrack);
  
  if (!advice) return null;

  const mixTypeInfo = MIX_TYPES[advice.mixType] || MIX_TYPES.blend;

  return (
    <div className={`mix-point-advisor ${isExpanded ? 'expanded' : ''}`}>
      {/* Compact View */}
      <div className="advisor-header" onClick={onToggle}>
        <div className="advisor-summary">
          {/* Mix Tip Label */}
          <div className="mix-tip-label">
            <Sparkles size={12} />
            <span>Mix Tip:</span>
          </div>
          <div 
            className="mix-type-badge"
            style={{ background: `${mixTypeInfo.color}20`, color: mixTypeInfo.color }}
          >
            <span className="mix-icon">{mixTypeInfo.icon}</span>
            <span>{mixTypeInfo.label}</span>
          </div>
          
          <div className="mix-duration">
            <Clock size={12} />
            <span>{advice.mixDuration}</span>
          </div>
          
          <div 
            className="difficulty-badge"
            style={{ background: `${DIFFICULTY_COLORS[advice.difficulty]}20`, color: DIFFICULTY_COLORS[advice.difficulty] }}
          >
            {advice.difficulty}
          </div>
        </div>
        
        <button className="expand-btn">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="advisor-details">
          {/* BPM & Energy Info */}
          <div className="transition-stats">
            <div className="stat">
              <span className="stat-label">BPM</span>
              <span className="stat-value">
                {advice.fromBpm} <ArrowRight size={12} /> {advice.toBpm}
                {advice.bpmDiff > 0 && (
                  <span className={`diff ${advice.bpmDiff > 5 ? 'warning' : ''}`}>
                    ({advice.bpmDiff > 0 ? '+' : ''}{advice.bpmDiff})
                  </span>
                )}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Energy</span>
              <span className="stat-value">
                <Zap size={12} />
                {advice.energyDiff > 0 ? `+${advice.energyDiff}` : advice.energyDiff}
                {advice.energyDiff > 2 && <span className="energy-up">↑ Building</span>}
                {advice.energyDiff < -2 && <span className="energy-down">↓ Dropping</span>}
              </span>
            </div>
          </div>

          {/* Mix Points */}
          <div className="mix-points">
            <div className="mix-point">
              <div className="mix-point-header">
                <Music size={14} />
                <span>Mix Out Point</span>
              </div>
              <div className="mix-point-time">{advice.mixPoints.out.time}</div>
              <div className="mix-point-desc">{advice.mixPoints.out.description}</div>
            </div>
            <div className="mix-point">
              <div className="mix-point-header">
                <Music size={14} />
                <span>Mix In Point</span>
              </div>
              <div className="mix-point-time">{advice.mixPoints.in.time}</div>
              <div className="mix-point-desc">{advice.mixPoints.in.description}</div>
            </div>
          </div>

          {/* Tips */}
          <div className="mix-tips">
            <div className="tips-header">
              <Sparkles size={14} />
              <span>DJ Tips</span>
            </div>
            <ul className="tips-list">
              {advice.tips.map((tip, idx) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </div>

          {/* Techniques */}
          {advice.technique.length > 0 && (
            <div className="techniques">
              <span className="techniques-label">Recommended techniques:</span>
              <div className="technique-tags">
                {advice.technique.map((tech, idx) => (
                  <span key={idx} className="technique-tag">
                    {tech.replace(/-/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MixPointAdvisor;
